import {useAppTranslation} from "services/i18n";
import {useAppDispatch} from "store";
import * as React from "react";
import {useCallback, useEffect, useMemo, useState} from "react";
import {Grid, Tooltip} from "@mui/material";
import {UserGroup} from "pages/UserGroupsPage";
import {CompanySpacesLearningEventLearningEventIdParticipantsGetRequest, ContentLearningEventParticipantIndexResponse} from "generated-api";
import {fetchLearningEventParticipants} from "store/userGroup";
import {SxProps} from "@mui/system";
import {NoteCell} from "components/userGroup/NoteCell";
import {EditParticipantNoteDialog} from "components/userGroup/EditParticipantNoteDialog";
import {TextFormField} from "components/form/TextFormField";
import {createCol, DataGrid, DataGridCol, DataGridMode, DataGridState, ItemsState, OrderDirType} from "components/DataGrid";
import {isApiResultError} from "../../helpers/api";

interface Participant extends ContentLearningEventParticipantIndexResponse {
}

const tableStyle: SxProps = {
    // marginTop: '32px',
    'th.MuiTableCell-head, th, td': {
        padding: '4px 4px',
        border: 'none', '.MuiFormControlLabel-root': {
            margin: 0
        }
    },
    '.MuiTableHead-root': {
        'th': {
            color: '#8D8D8D',
            fontWeight: 'normal',
            border: 'none !important',
            borderBottom: '1px solid #8D8D8D !important'
        }
    },
}

export interface ParticipantGridFilter extends Partial<CompanySpacesLearningEventLearningEventIdParticipantsGetRequest> {
    search: string
}

const defaultState: DataGridState<Participant, ParticipantGridFilter> = {
    orderCol: 'full_name',
    orderDir: OrderDirType.ASC,
    filter: {
        search: '',
    },
    pageSize: 20,
    page: 1
};

export const UserGroupParticipantsTab = ({userGroup}: { userGroup: UserGroup }) => {

    const t = useAppTranslation();
    const dispatch = useAppDispatch();

    const [participant, setParticipant] = useState<Participant>();

    const [dataGridState, setDataGridState] = useState(defaultState);
    const [itemsState, setItemsState] = useState<ItemsState<Participant>>({items: [], isLoading: true});

    const fetchEvent = useCallback(async () => {
        if (!userGroup.learning_event?.id) {
            return;
        }
        setItemsState(s => ({...s, isLoading: true}));
        const res = await dispatch(fetchLearningEventParticipants({
            // scope
            learningEventId: userGroup.learning_event.id,
            // filter
            ...dataGridState.filter,
            fullName: dataGridState.filter.search,
            // sort
            sort: dataGridState.orderCol ? dataGridState.orderCol + ':' + (dataGridState.orderDir || 'asc') : undefined,
            // paging
            perPage: dataGridState.pageSize,
            page: dataGridState.page
        }));
        if (!isApiResultError(res)) {
            const participants = (res.payload as Participant[] || []);
            setItemsState(s => ({
                ...s,
                items: dataGridState.page && dataGridState.page > 1
                    ? [...(s.items || []), ...participants]
                        .filter((item, i, items) =>
                            participants.find(o => o.id === item.id) === item || !participants.find(o => o.id === item.id))
                    : participants,
                isLoading: false
            }));
        }

    }, [dataGridState, userGroup, dispatch]);

    const cols = useMemo(() => {
        const cols: DataGridCol<Participant>[] = [
            createCol(t('userGroup.participants.name'), 'full_name', (_, item) =>
                <Tooltip title={item.email} enterDelay={500}><>{item.full_name}</>
                </Tooltip>),
            createCol(t('userGroup.participants.status'), 'courses_completed', (_, item) => <>
                {item.courses_completed ? t('userGroup.participants.completed') : t('userGroup.participants.notCompleted')}
                &nbsp;+&nbsp;
                {item.certificate_approved ? t('userGroup.participants.approved') : t('userGroup.participants.notApproved')}
            </>),
            createCol(t('userGroup.participants.certificate'), 'certificate_approved', (_, item) => <>
                {item.certificate_approved && item.courses_completed
                    ? <a href={'https://shattereddisk.github.io/rickroll/rickroll.mp4'} target={'_blank'} rel={'noreferrer'}>{t('userGroup.participants.showCertificate')}</a>
                    : '-'}
            </>),
            createCol(t('userGroup.participants.note'), 'note', (_, participant) => <NoteCell note={participant.note} handleEdit={() => setParticipant(participant)}/>)
        ];

        return cols.map(c => ({...c, orderCol: (c?.orderCol === 'full_name' ? c.orderCol : null)} as DataGridCol<Participant>));
    }, [t]);

    const createFilter = useCallback((formProps?: any) => {
        return <Grid container spacing={4} className={'tw-pb-16'}>
            {!!formProps && <Grid item lg={4} md={6} xs={12}>
                <TextFormField
                    name={'search'}
                    label={t('userGroup.participants.filter.search.label')}
                    placeholder={t('userGroup.participants.filter.search.placeholder')}
                    // tooltip={t('userGroup.participants.filter.search.hint')}
                />
            </Grid>}
        </Grid>;
    }, [t]);

    useEffect(() => {
        fetchEvent().then();
    }, [fetchEvent]);

    return <Grid container sx={{padding: '16px 24px', background: '#fff'}} rowSpacing={1}>
        <Grid item xs={12} sx={tableStyle}>
            <DataGrid
                createFilter={createFilter}
                cols={cols}
                state={dataGridState}
                defaultState={defaultState}
                setState={setDataGridState}
                itemsState={itemsState}
                mode={DataGridMode.SERVER}
                emptyListMessage={t('userGroup.participants.emptyList')}
                emptySearchMessage={t('userGroup.participants.emptySearch')}
                // actions={actions}
                // isActionMenu
                // rowClick={handleOpenUserGroup}
            />
        </Grid>
        {!!participant && !!participant.learning_event?.id && <EditParticipantNoteDialog participant={participant}
            learningEventId={participant.learning_event.id}
            onSuccess={fetchEvent}
            onClose={() => setParticipant(undefined)}/>}
    </Grid>;
}
